import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Mail,
  Search,
  RefreshCw,
  PenSquare,
  CornerUpLeft,
  Send,
  X,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/auth";

function Compose({ onClose, initial = {}, mode = "new" }) {
  const [to, setTo] = useState(initial.to || "");
  const [subject, setSubject] = useState(initial.subject || "");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!to.trim() || !body.trim()) {
      toast.error("Recipient and message are required");
      return;
    }
    setSending(true);
    try {
      if (mode === "reply") {
        await api.post("/oauth/google/gmail/reply", {
          thread_id: initial.thread_id,
          to,
          subject,
          body,
          in_reply_to: initial.in_reply_to || null,
        });
      } else {
        await api.post("/oauth/google/gmail/send", { to, subject, body });
      }
      toast.success(mode === "reply" ? "Reply sent" : "Email sent");
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-obsidian-900/80 p-4 backdrop-blur-sm" data-testid="gmail-compose-modal">
      <div className="gold-frame w-full max-w-xl rounded-2xl bg-obsidian-800 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl font-semibold text-ivory">
            {mode === "reply" ? "Reply" : "New email"}
          </h3>
          <button onClick={() => onClose(false)} data-testid="gmail-compose-close" className="text-ivory/60 hover:text-gold">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-6 space-y-4">
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="To"
            data-testid="gmail-compose-to"
            className="field"
          />
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            data-testid="gmail-compose-subject"
            className="field"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message…"
            rows={7}
            data-testid="gmail-compose-body"
            className="field resize-none"
          />
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={() => onClose(false)} className="rounded-full border border-gold/30 px-6 py-2.5 font-sans text-sm font-medium text-ivory/80 hover:text-ivory">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={sending}
            data-testid="gmail-compose-send"
            className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-2.5 font-sans text-sm font-semibold uppercase tracking-[0.1em] text-obsidian-900 transition-colors hover:bg-gold-light disabled:opacity-60"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GmailInbox() {
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [query, setQuery] = useState("");
  const [compose, setCompose] = useState(null);

  const connected = !!status?.connected;
  const gmailReady = !!status?.gmail_ready;

  const load = useCallback(async (q = "") => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/oauth/google/gmail/messages", { params: { q, limit: 25 } });
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load inbox");
      setMessages([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    api.get("/oauth/google/status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false, gmail_ready: false }));
  }, []);

  useEffect(() => {
    if (gmailReady) load();
  }, [gmailReady, load]);

  const open = async (m) => {
    setLoadingMsg(true);
    setSelected({ id: m.id, loading: true });
    try {
      const { data } = await api.get(`/oauth/google/gmail/messages/${m.id}`);
      setSelected(data);
      if (m.unread) {
        api.post(`/oauth/google/gmail/messages/${m.id}/read`).catch(() => {});
        setMessages((prev) => prev?.map((x) => (x.id === m.id ? { ...x, unread: false } : x)));
      }
    } catch (e) {
      toast.error("Could not open message");
      setSelected(null);
    } finally {
      setLoadingMsg(false);
    }
  };

  if (status === null) {
    return (
      <section className="gold-frame rounded-2xl bg-obsidian-800 p-8" data-testid="gmail-inbox-loading">
        <div className="flex items-center justify-center py-10 text-ivory/50">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </section>
    );
  }

  if (!connected || !gmailReady) {
    return (
      <section className="gold-frame rounded-2xl bg-obsidian-800 p-8" data-testid="gmail-inbox-locked">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Workspace Gmail</p>
        <h2 className="mt-3 font-display text-2xl font-semibold text-ivory">Business inbox</h2>
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-gold/20 bg-obsidian-900 p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <div>
            <p className="text-sm leading-relaxed text-ivory/70">
              {connected
                ? "Gmail access hasn't been granted yet. Reconnect Google Workspace and approve the Gmail permissions to read and reply to email here."
                : "Connect Google Workspace (panel on the right) and approve the Gmail permissions to read and reply to email here."}
            </p>
            {connected && (
              <p
                data-testid="gmail-reconnect-hint"
                className="mt-3 text-xs font-semibold uppercase tracking-[0.1em] text-gold"
              >
                Use “Connect Google Workspace” to re-authorize
              </p>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="gold-frame rounded-2xl bg-obsidian-800 p-5 sm:p-7" data-testid="gmail-inbox">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-gold" />
          <h2 className="font-display text-2xl font-semibold text-ivory">Business inbox</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load(query)} data-testid="gmail-refresh" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/25 text-gold hover:bg-gold hover:text-obsidian-900">
            <RefreshCw className={`h-4 w-4 ${loadingList ? "animate-spin" : ""}`} />
          </button>
          <button onClick={() => setCompose({ mode: "new" })} data-testid="gmail-compose-open" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-sans text-sm font-semibold uppercase tracking-[0.1em] text-obsidian-900 hover:bg-gold-light">
            <PenSquare className="h-4 w-4" /> Compose
          </button>
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); load(query); }}
        className="mt-5 flex items-center gap-2"
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ivory/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search mail (e.g. from:client is:unread)"
            data-testid="gmail-search-input"
            className="field pl-11"
          />
        </div>
        <button type="submit" data-testid="gmail-search-submit" className="rounded-full border border-gold/30 px-5 py-3 font-sans text-sm font-medium text-gold hover:bg-gold hover:text-obsidian-900">
          Search
        </button>
      </form>

      <div className="mt-6 grid gap-5 lg:grid-cols-12">
        {/* list */}
        <div className="lg:col-span-5" data-testid="gmail-message-list">
          {loadingList ? (
            <div className="flex items-center justify-center py-16 text-ivory/50"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : messages && messages.length === 0 ? (
            <p className="py-16 text-center text-sm text-ivory/50" data-testid="gmail-empty">No messages found.</p>
          ) : (
            <ul className="max-h-[560px] space-y-2 overflow-y-auto pr-1">
              {messages?.map((m) => (
                <li key={m.id}>
                  <button
                    onClick={() => open(m)}
                    data-testid={`gmail-message-${m.id}`}
                    className={`w-full rounded-xl border p-4 text-left transition-colors ${
                      selected?.id === m.id ? "border-gold/50 bg-obsidian-700" : "border-gold/12 bg-obsidian-900 hover:border-gold/30"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className={`truncate text-sm ${m.unread ? "font-semibold text-ivory" : "text-ivory/75"}`}>
                        {m.from}
                      </span>
                      {m.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-gold" />}
                    </div>
                    <p className={`mt-1 truncate text-sm ${m.unread ? "text-ivory" : "text-ivory/70"}`}>{m.subject || "(no subject)"}</p>
                    <p className="mt-1 truncate text-xs text-ivory/45">{m.snippet}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* detail */}
        <div className="lg:col-span-7" data-testid="gmail-message-detail">
          {!selected ? (
            <div className="flex h-full min-h-[260px] items-center justify-center rounded-xl border border-dashed border-gold/20 text-sm text-ivory/45">
              Select a message to read
            </div>
          ) : loadingMsg || selected.loading ? (
            <div className="flex h-full min-h-[260px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-ivory/50" /></div>
          ) : (
            <div className="rounded-xl border border-gold/15 bg-obsidian-900 p-5 sm:p-6">
              <h3 className="font-display text-xl font-semibold text-ivory">{selected.subject || "(no subject)"}</h3>
              <p className="mt-2 text-sm text-gold">{selected.from}</p>
              <p className="text-xs text-ivory/40">{selected.date}</p>
              <div className="mt-5 max-h-[360px] overflow-y-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-ivory/80">
                {selected.text || selected.snippet || "(no text content)"}
              </div>
              <button
                onClick={() =>
                  setCompose({
                    mode: "reply",
                    to: selected.from,
                    subject: selected.subject || "",
                    thread_id: selected.thread_id,
                    in_reply_to: selected.message_id_header || null,
                  })
                }
                data-testid="gmail-reply-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-2.5 font-sans text-sm font-semibold uppercase tracking-[0.1em] text-gold transition-colors hover:bg-gold hover:text-obsidian-900"
              >
                <CornerUpLeft className="h-4 w-4" /> Reply
              </button>
            </div>
          )}
        </div>
      </div>

      {compose && (
        <Compose
          mode={compose.mode}
          initial={compose}
          onClose={(sent) => {
            setCompose(null);
            if (sent) load(query);
          }}
        />
      )}
    </section>
  );
}
